import { promises as fs } from "node:fs";
import path from "node:path";
import { fileURLToPath } from "node:url";

import type { Logger } from "pino";

const ROLES_DIR_NAME = ".roles";
// Distinct from the brand seeder's `schema-templates` on purpose: in the
// installer bundle every module collapses into `dist/worker.js`, so both
// seeders resolve `import.meta.url` to the same dir. A shared name would
// make the recursive walk in this seeder pick up brand `.md` files. Keep
// these namespaces separate.
const TEMPLATES_DIR_NAME = "role-templates";

/**
 * Roles we used to ship but no longer do. Listed here so the seeder can
 * prune them from users' `~/.appostle/.roles/` on boot — the seeder never
 * deletes by default (only files we currently ship get overwritten), so
 * removed templates would otherwise linger forever as stale prompts.
 *
 * Only list paths Appostle itself shipped. User-authored roles must never
 * appear here.
 */
const DEPRECATED_ROLE_PATHS: readonly string[] = [
  "builder/astro-website-v1.md",
  "builder/nextjs-website-v1.1.md",
  "builder/nextjs-website-v1.2.md",
  "scraper/website-via-source-v1.1.md",
  "scraper/website-via-url-v1.1.md",
  "scraper/website-via-source-v2.md",
  "scraper/website-via-url-v2.md",
];

/**
 * Resolves the on-disk location of the bundled role templates.
 *
 * Co-located with this file in both source (`src/server/roles/role-templates`)
 * and compiled output (`dist/server/server/roles/role-templates`) — the build
 * pipeline copies the `.md` tree alongside the emitted `.js`, so a sibling
 * lookup works in dev (tsx) and prod (node dist) without branching. In the
 * installer bundle the same lookup resolves to `dist/role-templates` because
 * `import.meta.url` points to `dist/worker.js`.
 */
function resolveTemplatesDir(): string {
  const here = path.dirname(fileURLToPath(import.meta.url));
  return path.join(here, TEMPLATES_DIR_NAME);
}

interface SeedOptions {
  logger?: Logger;
}

interface SeedResult {
  targetDir: string;
  written: number;
  files: string[];
}

/**
 * Seed Appostle's bundled global roles into `<appostleHome>/.roles/`.
 *
 * Semantics differ from the brand seeder on purpose: bundled roles are
 * Appostle property. We always overwrite the seeded files on every daemon
 * boot so customers stay on the latest prompts. User-authored roles (any
 * `.md` outside the seeded paths) are left untouched — we never enumerate or
 * delete anything we did not ship.
 *
 * Idempotent in the sense that running it twice converges to the same
 * filesystem state; it is *not* creation-only.
 */
export async function seedGlobalRoles(
  appostleHome: string,
  options?: SeedOptions,
): Promise<SeedResult> {
  const target = path.join(appostleHome, ROLES_DIR_NAME);
  const templatesDir = resolveTemplatesDir();

  let templateEntries: string[];
  try {
    templateEntries = await collectMarkdownFiles(templatesDir);
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      options?.logger?.warn(
        { templatesDir },
        "global role templates directory missing; skipping seed",
      );
      return { targetDir: target, written: 0, files: [] };
    }
    throw error;
  }

  await fs.mkdir(target, { recursive: true });

  const written: string[] = [];
  for (const absSource of templateEntries) {
    const relative = path.relative(templatesDir, absSource);
    const destination = path.join(target, relative);
    await fs.mkdir(path.dirname(destination), { recursive: true });
    await fs.copyFile(absSource, destination);
    written.push(relative);
  }

  const removed = await pruneDeprecatedRoles(target, options?.logger);

  options?.logger?.info(
    { targetDir: target, count: written.length, files: written, removed },
    "global roles seeded",
  );

  return { targetDir: target, written: written.length, files: written };
}

/**
 * Delete previously-shipped role files we no longer bundle. The seeder is
 * always-overwrite for files we currently ship, but never enumerates +
 * deletes — so a removed template would persist forever in every user's
 * home as a stale prompt. This explicit allow-list closes that gap.
 *
 * Missing files are fine (most users won't have all of them). Anything not
 * in `DEPRECATED_ROLE_PATHS` is untouched, preserving the rule that we
 * never delete user-authored roles.
 */
async function pruneDeprecatedRoles(
  rolesDir: string,
  logger: Logger | undefined,
): Promise<string[]> {
  const removed: string[] = [];
  for (const relative of DEPRECATED_ROLE_PATHS) {
    const absolute = path.join(rolesDir, relative);
    try {
      await fs.unlink(absolute);
      removed.push(relative);
    } catch (error) {
      const code = (error as NodeJS.ErrnoException).code;
      if (code === "ENOENT") {
        continue;
      }
      logger?.warn({ err: error, file: absolute }, "failed to prune deprecated role");
    }
  }
  return removed;
}

/**
 * Walks `dir` recursively and returns the absolute paths of every `.md`
 * file. Templates are organized by `<category>/<name>.md`, so this needs to
 * recurse — the brand seeder's flat readdir is not enough.
 */
async function collectMarkdownFiles(dir: string): Promise<string[]> {
  const results: string[] = [];
  const stack: string[] = [dir];

  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) {
      continue;
    }
    const entries = await fs.readdir(current, { withFileTypes: true });
    for (const entry of entries) {
      const absolute = path.join(current, entry.name);
      if (entry.isDirectory()) {
        stack.push(absolute);
      } else if (entry.isFile() && entry.name.endsWith(".md")) {
        results.push(absolute);
      }
    }
  }

  return results.sort();
}
